/**
 * CoachPlaybook - Login Page
 * Version: 0.7.0
 * Description: Google OAuth login page with feature overview and dark theme
 */

import React from 'react';
import { useAuth } from '../../utils/auth/AuthContext';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 50%, #0f172a 100%)',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
  },
  card: {
    background: '#1e293b',
    borderRadius: '16px',
    padding: '40px',
    maxWidth: '480px',
    width: '100%',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    border: '1px solid #334155',
    textAlign: 'center'
  },
  logo: {
    fontSize: '48px',
    marginBottom: '8px'
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#f8fafc',
    margin: '0 0 8px 0'
  },
  subtitle: {
    fontSize: '14px',
    color: '#94a3b8',
    margin: '0 0 32px 0',
    lineHeight: '1.5'
  },
  googleBtn: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '10px',
    background: '#ffffff',
    color: '#1f2937',
    border: 'none',
    borderRadius: '10px',
    padding: '14px 32px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    width: '100%',
    transition: 'background 0.2s, transform 0.1s',
    marginBottom: '32px'
  },
  divider: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    margin: '0 0 24px 0'
  },
  dividerLine: {
    flex: 1,
    height: '1px',
    background: '#334155'
  },
  dividerText: {
    fontSize: '12px',
    color: '#64748b',
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontWeight: '600'
  },
  featuresGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '12px'
  },
  featureCard: {
    background: '#0f172a',
    borderRadius: '10px',
    padding: '16px 12px',
    border: '1px solid #1e293b',
    textAlign: 'left'
  },
  featureIcon: {
    fontSize: '20px',
    marginBottom: '6px'
  },
  featureName: {
    fontSize: '13px',
    fontWeight: '600',
    color: '#e2e8f0',
    margin: '0 0 4px 0'
  },
  featureDesc: {
    fontSize: '11px',
    color: '#64748b',
    margin: 0,
    lineHeight: '1.4'
  },
  premiumBadge: {
    display: 'inline-block',
    background: '#7c3aed',
    color: '#ffffff',
    fontSize: '9px',
    fontWeight: '700',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '6px',
    verticalAlign: 'middle',
    textTransform: 'uppercase',
    letterSpacing: '0.5px'
  },
  footer: {
    marginTop: '24px',
    fontSize: '11px',
    color: '#475569'
  }
};

const features = [
  { icon: '\uD83C\uDFC8', name: 'Play Designer', desc: 'Draw plays for any sport with drag-and-drop', free: true },
  { icon: '\uD83D\uDCCB', name: 'Roster Manager', desc: 'Track players, positions, and jersey numbers', free: true },
  { icon: '\uD83D\uDCCA', name: 'Game Stats', desc: 'Record and analyze game statistics', free: true },
  { icon: '\uD83D\uDD0D', name: 'Scouting', desc: 'Scout opponents and track tendencies', free: true },
  { icon: '\u2601\uFE0F', name: 'Cloud Sync', desc: 'Sync playbooks across all devices', free: false },
  { icon: '\uD83D\uDCC8', name: 'Advanced Analytics', desc: 'Deep stat breakdowns and trends', free: false }
];

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
  </svg>
);

const LoginPage = () => {
  const { login } = useAuth();

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>{'\uD83C\uDFC8'}</div>
        <h1 style={styles.title}>CoachPlaybook</h1>
        <p style={styles.subtitle}>
          The all-in-one coaching platform. Design plays, manage rosters, track stats, and scout opponents — all from your phone.
        </p>

        <button
          style={styles.googleBtn}
          onClick={login}
          onMouseEnter={(e) => { e.target.style.background = '#f1f5f9'; }}
          onMouseLeave={(e) => { e.target.style.background = '#ffffff'; }}
        >
          <GoogleIcon />
          Sign in with Google
        </button>

        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>Features</span>
          <div style={styles.dividerLine} />
        </div>

        <div style={styles.featuresGrid}>
          {features.map((f) => (
            <div key={f.name} style={styles.featureCard}>
              <div style={styles.featureIcon}>{f.icon}</div>
              <p style={styles.featureName}>
                {f.name}
                {!f.free && <span style={styles.premiumBadge}>Premium</span>}
              </p>
              <p style={styles.featureDesc}>{f.desc}</p>
            </div>
          ))}
        </div>

        <p style={styles.footer}>
          Free tier includes core features. Premium unlocks cloud sync and advanced analytics.
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
